import { useEffect, useRef } from 'react';
import { useEsriMapContext } from '../EsriMapContext';
import type ScaleBar from '@arcgis/core/widgets/ScaleBar';
import type Locate from '@arcgis/core/widgets/Locate';
import type Geometry from '@arcgis/core/geometry/Geometry';
import type Graphic from '@arcgis/core/Graphic';
import type Extent from '@arcgis/core/geometry/Extent';
import { functionalityEnabled } from '../../utils';
import { MapFunction } from '../../types';
import logger from '@Map/logger/Logger';

export interface UseControlsOptions {
	/** When provided, fitToExtent() with no target will call this (e.g. viewport resetPosition) instead of view.goTo(view.extent). */
	resetPosition?: () => void;
}

export const useControls = (options: UseControlsOptions = {}) => {
	const { view, displayMode } = useEsriMapContext();
	const { resetPosition } = options;

	const scaleBarRef = useRef<ScaleBar | null>(null);
	const locateRef = useRef<Locate | null>(null);

	// Add ScaleBar and Locate widgets when view is ready and functionality is enabled (same logic as index-esri).
	useEffect(() => {
		if (!view) return;

		let scaleBar: ScaleBar | undefined;
		let locate: Locate | undefined;

		const scaleEnabled = functionalityEnabled(
			MapFunction.scaleControl,
			displayMode,
		);
		const locateEnabled = functionalityEnabled(
			MapFunction.geolocateControl,
			displayMode,
		);

		if (!scaleEnabled && !locateEnabled) return;

		Promise.all([
			import('@arcgis/core/widgets/ScaleBar.js'),
			import('@arcgis/core/widgets/Locate.js'),
		]).then(([{ default: ScaleBar }, { default: Locate }]) => {
			if (scaleEnabled) {
				scaleBar = new ScaleBar({ view, unit: 'metric' });
				view.ui.add(scaleBar, { position: 'bottom-left' });
				scaleBarRef.current = scaleBar;
			}
			if (locateEnabled) {
				locate = new Locate({
					view,
					geolocationOptions: { enableHighAccuracy: true },
				});
				locateRef.current = locate;
			}
		});

		return () => {
			if (scaleBarRef.current) {
				view.ui.remove(scaleBarRef.current);
				scaleBarRef.current = null;
			}
			if (locateRef.current) {
				view.ui.remove(locateRef.current);
				locateRef.current = null;
			}
		};
	}, [view, displayMode]);

	return {
		zoomIn(): void {
			if (!view) return;
			view.goTo({ zoom: view.zoom + 1 });
		},

		zoomOut(): void {
			if (!view) return;
			view.goTo({ zoom: view.zoom - 1 });
		},

		setZoom(level: number): void {
			if (!view) return;
			view.goTo({ zoom: level });
		},

		async locateUser(): Promise<void> {
			locateRef.current?.locate();
		},

		fitToExtent(target?: Geometry | Graphic | Extent): void {
			if (!view) return;
			if (target == null && resetPosition) {
				resetPosition();
				return;
			}
			if (target) {
				view.goTo(target as any);
				return;
			}
			logger.warn('No target provided to fitToExtent');
		},

		toggle3DView(): void {
			// TODO: swap MapView ↔ SceneView on the same Map instance
		},
	};
};

export type Controls = ReturnType<typeof useControls>;
